import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { normalizeWord } from "../lib/gridReveal";

const CLEAR_MS_NEW = 1100;
const CLEAR_MS_ALREADY = 900;
const STAGGER_MS = 55;

/**
 * Android PlayScreen parity — celebrate grid cells when a word is found / re-found.
 */
export default function useWordRevealCelebrate(wordPositions) {
    const [celebratingCellKeys, setCelebratingCellKeys] = useState(() => new Set());
    const [celebrateOrder, setCelebrateOrder] = useState([]);
    const [celebrateMode, setCelebrateMode] = useState("new");
    const [revealBurstId, setRevealBurstId] = useState(0);
    const clearRef = useRef(null);

    useEffect(
        () => () => {
            if (clearRef.current) window.clearTimeout(clearRef.current);
        },
        [],
    );

    const triggerCellRevealEffect = useCallback((keys, mode = "new") => {
        const cellKeys = (keys || []).filter(Boolean);
        if (!cellKeys.length) return;
        if (clearRef.current) {
            window.clearTimeout(clearRef.current);
            clearRef.current = null;
        }
        setCelebrateMode(mode);
        setCelebrateOrder(cellKeys);
        setCelebratingCellKeys(new Set(cellKeys));
        setRevealBurstId((id) => id + 1);
        clearRef.current = window.setTimeout(() => {
            setCelebratingCellKeys(new Set());
            setCelebrateOrder([]);
            setCelebrateMode("new");
            clearRef.current = null;
        }, mode === "already" ? CLEAR_MS_ALREADY : CLEAR_MS_NEW);
    }, []);

    const triggerWordRevealEffect = useCallback(
        (word, mode = "new") => {
            const positions = wordPositions?.[normalizeWord(word)];
            if (!positions?.length) return;
            triggerCellRevealEffect(
                positions.map((p) => `${p.row},${p.col}`),
                mode,
            );
        },
        [wordPositions, triggerCellRevealEffect],
    );

    const orderIndex = useMemo(() => {
        const map = new Map();
        celebrateOrder.forEach((key, i) => map.set(key, i));
        return map;
    }, [celebrateOrder]);

    return {
        celebratingCellKeys,
        celebrateOrder,
        celebrateMode,
        revealBurstId,
        orderIndex,
        staggerMs: STAGGER_MS,
        triggerWordRevealEffect,
        triggerCellRevealEffect,
    };
}

/** Build burst origins in % for a full or cropped board. */
export function burstOriginsForGrid(cellKeys, gridSize, bounds = null) {
    const size = Math.max(1, Number(gridSize) || 1);
    const minRow = bounds?.minRow ?? 0;
    const minCol = bounds?.minCol ?? 0;
    const rows = Math.max(1, (bounds?.maxRow ?? size - 1) - minRow + 1);
    const cols = Math.max(1, (bounds?.maxCol ?? size - 1) - minCol + 1);
    return [...cellKeys].map((key) => {
        const [row, col] = String(key).split(",").map(Number);
        return {
            key,
            xPct: ((col - minCol + 0.5) / cols) * 100,
            yPct: ((row - minRow + 0.5) / rows) * 100,
        };
    });
}

/** Cell celebrate styles — Android RevealCell 'new' / 'already' parity via CSS. */
export function celebrateCellSx({ isCelebrating, celebrateMode, order, staggerMs }) {
    if (!isCelebrating) return null;
    const delay = (order ?? 0) * (staggerMs ?? STAGGER_MS);
    const isAlready = celebrateMode === "already";
    return {
        zIndex: 4,
        ...(isAlready
            ? {
                  bgcolor: "#FFF0D6 !important",
                  borderColor: "#E8A855 !important",
                  borderWidth: "2.5px !important",
                  boxShadow: "0 2px 10px rgba(232, 168, 85, 0.45)",
                  "@keyframes wwCellAlready": {
                      "0%, 100%": { transform: "translateY(0) scale(1)" },
                      "20%": { transform: "translateY(-3px) scale(1.12)" },
                      "45%": { transform: "translateY(0) scale(1)" },
                      "70%": { transform: "translateY(-2px) scale(1.08)" },
                  },
                  animation: `wwCellAlready 760ms ease-in-out ${delay}ms both`,
              }
            : {
                  bgcolor: "#FFE8CC !important",
                  borderColor: "#E07A1A !important",
                  boxShadow: "0 2px 14px rgba(224, 122, 26, 0.45)",
                  "@keyframes wwCellCelebrate": {
                      "0%": { transform: "translateY(0) scale(1)" },
                      "22%": { transform: "translateY(-6px) scale(1.28)" },
                      "48%": { transform: "translateY(0) scale(0.94)" },
                      "72%": { transform: "translateY(-2px) scale(1.08)" },
                      "100%": { transform: "translateY(0) scale(1)" },
                  },
                  animation: `wwCellCelebrate 900ms cubic-bezier(0.22, 1.2, 0.36, 1) ${delay}ms both`,
              }),
    };
}
